import PropTypes from "prop-types"
import React, { useEffect } from "react"

import AlertBanner from "src/components/AlertBanner"
import Divider from "src/components/Divider"
import Heading from "src/components/Heading"

import { useConfettiOnce } from "src/hooks/use_confetti_once"
import { useTracker } from "src/hooks/use_tracker"

import ReachOut from "./ReachOut"
import AccountSubmitted from "./Submitted/AccountSubmitted"
import CreateAccountCard from "./Submitted/CreateAccountCard"
import SignInCard from "./Submitted/SignInCard"
import SigningFailedNotice from "./Submitted/SigningFailedNotice"
import useSigningStatus from "./Submitted/useSigningStatus"

const ContractSubmitted = ({
  account,
  contractChatUrl,
  copy,
  createAccountUrl,
  delayedCopy,
  failedCopy,
  finalizingCopy,
  marina,
  quoteId,
  reachOut,
  signInUrl,
  signingStatus,
  slot,
  subtitle,
  title,
  unavailableCopy,
}) => {
  const tracker = useTracker()
  const { status, reservationUrl } = useSigningStatus({
    quoteId,
    initialStatus: signingStatus,
    initialReservationUrl: account?.reservationUrl ?? null,
  })
  const isFailed = status === "failed"
  const isStalled = status === "delayed" || status === "unavailable"
  const isFinalizing = status === "processing" && slot !== "account"
  const offerHeldBack = isFailed || isStalled

  useConfettiOnce(quoteId, Boolean(slot) && status === "completed")

  useEffect(() => {
    if (!marina || !quoteId) return

    tracker.trackEvent("contracts_v2:confirmation_viewed", {
      slot: slot || "none",
      contract_quote_id: quoteId,
      marina_id: marina.id,
      marina_name: marina.name,
    })
  }, [tracker, slot, quoteId, marina])

  const renderNotice = () => {
    if (isFailed) {
      return (
        <SigningFailedNotice
          contractChatUrl={contractChatUrl}
          copy={failedCopy}
          marina={marina}
          quoteId={quoteId}
          reachOut={reachOut}
        />
      )
    }

    if (!isStalled) return null

    return (
      <div className="flex flex-col gap-4">
        <AlertBanner type="info">
          {status === "delayed" ? delayedCopy : unavailableCopy}
        </AlertBanner>
        {reachOut && marina && contractChatUrl && (
          <ReachOut
            reachOut={reachOut}
            contractChatUrl={contractChatUrl}
            marinaId={marina.id}
            marinaName={marina.name}
            quoteId={quoteId}
          />
        )}
      </div>
    )
  }

  const renderSlot = () => {
    if (isFinalizing) {
      return (
        <div className="flex items-center gap-2 text-gray-700">
          <i className="fa-solid fa-spinner fa-spin" />
          <span>{finalizingCopy}</span>
        </div>
      )
    }

    switch (slot) {
      case "createAccount":
        if (offerHeldBack) return null

        return (
          <>
            <p className="mb-4 text-sm font-semibold text-gray-700">
              {copy.whatsNext}
            </p>
            <CreateAccountCard
              copy={copy}
              createAccountUrl={createAccountUrl}
              marina={marina}
              quoteId={quoteId}
            />
          </>
        )
      case "signIn":
        if (offerHeldBack) return null

        return (
          <SignInCard
            copy={copy}
            marina={marina}
            quoteId={quoteId}
            signInUrl={signInUrl}
          />
        )
      case "account":
        return (
          <AccountSubmitted
            boat={account.boat}
            contractChatUrl={account.contractChatUrl}
            copy={copy}
            downloadPdfUrl={account.downloadPdfUrl}
            homeport={account.homeport}
            homeportIsMarina={account.homeportIsMarina}
            marina={marina}
            mobileWebview={account.mobileWebview}
            quoteId={quoteId}
            reservationUrl={reservationUrl}
            savedPdfUrl={account.savedPdfUrl}
            status={status}
          />
        )
      case "signedInOther":
        return <p className="text-gray-700">{copy.body}</p>
      default:
        return null
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-col items-start gap-2 lg:flex-row-reverse lg:items-center lg:justify-end lg:gap-3">
        {status === "completed" && (
          <i className="fa-solid fa-circle-check text-7xl text-teal-400" />
        )}
        <Heading.HeroTitle>
          {isFailed ? failedCopy.title : title}
        </Heading.HeroTitle>
      </div>
      {!isFailed && subtitle && (
        <p className="mb-6 mt-2 text-base font-semibold text-gray-900">
          {subtitle}
        </p>
      )}
      <Divider />
      <div className="mt-6 flex flex-col gap-6">
        {renderNotice()}
        {slot && renderSlot()}
      </div>
    </div>
  )
}

ContractSubmitted.propTypes = {
  account: PropTypes.object,
  contractChatUrl: PropTypes.string,
  copy: PropTypes.object,
  createAccountUrl: PropTypes.string,
  delayedCopy: PropTypes.string,
  failedCopy: PropTypes.shape({
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
  }),
  finalizingCopy: PropTypes.string,
  marina: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    image: PropTypes.string,
  }),
  quoteId: PropTypes.string,
  reachOut: PropTypes.shape({
    design: PropTypes.oneOf(["legacy", "needHelp"]).isRequired,
    mode: PropTypes.oneOf(["message", "call", "none"]).isRequired,
    phone: PropTypes.string,
    phoneUri: PropTypes.string,
  }),
  signInUrl: PropTypes.string,
  signingStatus: PropTypes.oneOf(["completed", "failed", "processing"]),
  slot: PropTypes.oneOf([
    "createAccount",
    "signIn",
    "signedInOther",
    "account",
  ]),
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  unavailableCopy: PropTypes.string,
}

export default ContractSubmitted
